import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { useState } from "react"
import { FormProvider, useFieldArray, useForm } from "react-hook-form"

import { useGetAssociatedProductIds } from "../../hooks/useGetAssociatedProductIds"

import { AssociateProductCustomerInfoStep } from "./AssociateProductCustomerInfoStep"
import { AssociateProductLocationsStep } from "./AssociateProductLocationsStep"
import { AssociateProductSearchStep } from "./AssociateProductSearchStep"

import { Button } from "@/components/common/Button"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import type { Product } from "@/graphql/codegen/graphql"
import { useGetLocations } from "@/hooks/useGetLocations"
import { EMPTY_LOCATION } from "@/screens/CustomerProducts/constants"
import { useAssociateProduct } from "@/screens/Products/hooks/useAssociateProduct"
import { associateProductFormSchema } from "@/screens/Products/schemas/associateProductForm"
import type { AssociateProductFormValues } from "@/screens/Products/schemas/associateProductForm"
import type { Option } from "@/types/form-fields"

enum AssociationDialogStep {
  Search = 1,
  CustomerInfo = 2,
  Locations = 3,
}

const ASSOCIATION_DIALOG_STEPS = [
  AssociationDialogStep.Search,
  AssociationDialogStep.CustomerInfo,
  AssociationDialogStep.Locations,
]

interface AssociateProductDialogProps {
  companyId: string
  onClose: (shouldRefetch?: boolean) => void
}

export const AddProductAssociationDialog: FC<AssociateProductDialogProps> = ({ companyId, onClose }) => {
  const { associatedProductIds } = useGetAssociatedProductIds(companyId)
  const [step, setStep] = useState(AssociationDialogStep.Search)
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const [submittedQuery, setSubmittedQuery] = useState("")
  const [showInactiveProducts, setShowInactiveProducts] = useState(false)
  const [searchInputValue, setSearchInputValue] = useState("")

  const { associateProduct, isSavingAssociation } = useAssociateProduct()
  const { locations } = useGetLocations(companyId, { isLocationActive: true })

  const locationOptions: Option<string>[] = locations.map((loc) => ({
    value: loc.locationId,
    label: loc.name,
  }))

  const methods = useForm<AssociateProductFormValues>({
    resolver: zodResolver(associateProductFormSchema),
    mode: "onChange",
    defaultValues: {
      customerDescription: "",
      customerCode: [],
      locations: [],
    },
  })

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = methods
  const { fields, append, remove } = useFieldArray({ control, name: "locations" })

  const handleContinue = () => {
    switch (step) {
      case AssociationDialogStep.Search:
        if (!selectedProduct) return
        reset({
          productId: selectedProduct.productId,
          customerDescription: "",
          customerCode: [],
          locations: [],
        })
        setStep(AssociationDialogStep.CustomerInfo)
        break
      case AssociationDialogStep.CustomerInfo:
        setStep(AssociationDialogStep.Locations)
        break
    }
  }

  const handleBack = () => {
    const currentIndex = ASSOCIATION_DIALOG_STEPS.indexOf(step)
    if (currentIndex > 0) setStep(ASSOCIATION_DIALOG_STEPS[currentIndex - 1])
  }

  const handleCancel = () => {
    onClose()
  }

  const handleAddLocation = () => {
    append(EMPTY_LOCATION)
  }

  const onSubmit = async (formValues: AssociateProductFormValues) => {
    const success = await associateProduct({
      companyId,
      productId: formValues.productId,
      customerDescription: formValues.customerDescription || undefined,
      customerCode: formValues.customerCode.length ? formValues.customerCode : undefined,
      locations: formValues.locations.map((loc) => ({
        shipToLocationId: loc.shipToLocationId,
        customerCode: loc.customerCode.length ? loc.customerCode : undefined,
        priorityStatus: loc.priorityStatus ?? undefined,
        approvalStatus: loc.approvalStatus ?? undefined,
      })),
    })

    if (success) {
      onClose(true)
    }
  }

  return (
    <Dialog
      open
      onClose={handleCancel}
      fullWidth
      sx={{ "& .MuiDialog-container": { "& .MuiPaper-root": { maxWidth: "1072px", width: "100%" } } }}
      data-testid='associate-product-dialog'
    >
      <FormProvider {...methods}>
        <DialogHeader title='Product Association' onClose={handleCancel} />

        <DialogContent classes={{ root: "p-10" }}>
          {step === AssociationDialogStep.Search && (
            <AssociateProductSearchStep
              associatedProductIds={associatedProductIds}
              selectedProduct={selectedProduct}
              onProductSelect={setSelectedProduct}
              submittedQuery={submittedQuery}
              onSubmittedQueryChange={setSubmittedQuery}
              showInactive={showInactiveProducts}
              onShowInactiveChange={setShowInactiveProducts}
              searchInputValue={searchInputValue}
              onSearchInputValueChange={setSearchInputValue}
            />
          )}

          {step === AssociationDialogStep.CustomerInfo && selectedProduct && (
            <AssociateProductCustomerInfoStep product={selectedProduct} />
          )}

          {step === AssociationDialogStep.Locations && selectedProduct && (
            <AssociateProductLocationsStep
              product={selectedProduct}
              fields={fields}
              locationOptions={locationOptions}
              onAddLocation={handleAddLocation}
              onRemoveLocation={remove}
            />
          )}
        </DialogContent>

        <DialogActions className='flex justify-between p-10'>
          <Button appearance='outlined' onClick={handleCancel} data-testid='associate-product-cancel-button'>
            Cancel
          </Button>

          <Box className='flex gap-2'>
            {step > AssociationDialogStep.Search && (
              <Button appearance='text' onClick={handleBack} data-testid='associate-product-back-button'>
                ‹ Go Back
              </Button>
            )}

            {step < AssociationDialogStep.Locations && (
              <Button
                onClick={handleContinue}
                disabled={step === AssociationDialogStep.Search && !selectedProduct}
                data-testid='associate-product-continue-button'
              >
                Continue
              </Button>
            )}

            {step === AssociationDialogStep.Locations && (
              <Button
                onClick={handleSubmit(onSubmit)}
                disabled={!isValid || isSavingAssociation}
                loading={isSavingAssociation}
                data-testid='associate-product-save-button'
              >
                Save
              </Button>
            )}
          </Box>
        </DialogActions>
      </FormProvider>
    </Dialog>
  )
}
